import React from 'react';
import { Plus, ChevronsDown, ChevronsUp } from 'lucide-react';
import Dropdown from 'react-bootstrap/Dropdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { isStickyHeaderStuck } from '../utils/stickyHeader';
import DomainMappingSearchBar from './DomainMappingSearchBar';

const colors = ['#FA4616', '#00c7d4', '#ffc700', '#4d9ff9', '#915cff', '#ff6c7c'];

const envToggle = React.forwardRef(({ onClick, selectedEnv }, ref) => (
    <div
        ref={ref}
        className="qd-env-dropdown__btn dropdown-toggle !tw-rounded-lg"
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
    >
        <span className="env-tag !tw-rounded-lg" style={{ backgroundColor: selectedEnv?.color || colors[0] }}>
            {selectedEnv?.label ? selectedEnv.label[0].toUpperCase() : 'E'}
        </span>
        <span className="env-info">
            <label className='text-dark tw-opacity-75'>Environment</label>
            <strong className="env-name text-dark tw-min-w-[100px]">{selectedEnv?.label || 'Select Environment'}</strong>
        </span>
    </div>
));

class DomainMappingToolbar extends React.Component {
    constructor(props) {
        super(props);
        this.sentinelRef = React.createRef();
        this.state = { isStuck: false, envSearchText: '' };
    }

    componentDidMount() {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        if (typeof IntersectionObserver !== 'undefined' && this.sentinelRef.current) {
            this.observer = new IntersectionObserver(() => this.handleScroll(), { threshold: [0, 1] });
            this.observer.observe(this.sentinelRef.current);
        }
        this.handleScroll();
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        if (this.observer) this.observer.disconnect();
    }

    handleScroll = () => {
        const isStuck = isStickyHeaderStuck(this.sentinelRef.current);
        if (isStuck !== this.state.isStuck) {
            this.setState({ isStuck });
        }
    };

    render() {
        const {
            businesses = [],
            functions = [],
            kpis = [],
            searchVal,
            onSearchChange,
            onAddMappingClick,
            filterBusinesses,
            onBusinessFilterToggle,
            filterFunctions,
            onFunctionFilterChange,
            filterKpis,
            onKpiFilterChange,
            filterStatus,
            onStatusFilterChange,
            clearAllFilters,
            matchingCount,
            isFullyExpanded,
            onExpandAll,
            onCollapseAll,
            showMappedKpis = true,
            onShowMappedToggle,
            environments = [],
            filterEnvironment,
            onEnvironmentChange
        } = this.props;

        const rawEnvironments = Array.isArray(environments) && environments.length > 0 ? environments : [
            { label: 'dev', value: '1' },
            { label: 'test', value: '2' },
            { label: 'Staging', value: '3' },
            { label: 'Production', value: '4' }
        ];

        const envOptions = rawEnvironments.map((env, index) => {
            const label = typeof env === 'string' ? env : (env.label || env.env_name || env.name || String(env.value || env.id || ''));
            const value = typeof env === 'string' ? env : (env.value !== undefined ? String(env.value) : (env.env_id !== undefined ? String(env.env_id) : String(env.id || label)));
            return {
                label,
                value,
                color: colors[index % colors.length]
            };
        });

        const selectedEnvOption = (filterEnvironment && filterEnvironment !== 'all')
            ? (envOptions.find(opt => String(opt.value) === String(filterEnvironment) || String(opt.label) === String(filterEnvironment)) || envOptions[0])
            : envOptions[0];

        const filteredEnvOptions = envOptions.filter(e => 
            !this.state.envSearchText || e.label.toLowerCase().includes(this.state.envSearchText.toLowerCase())
        );

        return (
            <>
                <div ref={this.sentinelRef} className="tw-h-0 tw-w-full tw-pointer-events-none" />
                <div className={`tw-bg-white dark:tw-bg-[#1E1E2D] tw-border-0 tw-border-b tw-border-solid tw-border-[#ECECEC] dark:tw-border-white/10 tw-sticky tw-top-[64px] tw-z-20 ${this.state.isStuck ? 'sticky-header-shadow' : ''}`}>
                <div className="tw-p-4 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-justify-between tw-gap-3 md:tw-gap-4">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-shrink-0">
                        <div className="qd-header__topbar-item qd-env-dropdown qd-env--light tw-shrink-0">
                            <Dropdown>
                                <Dropdown.Toggle as={envToggle} selectedEnv={selectedEnvOption} />
                                <Dropdown.Menu>
                                    <div className="qd-env-dropdown-menu__head">
                                        <div className="qd-search-bar">
                                            <div className="input-group">
                                                <div className="input-group-prepend">
                                                    <button id="button-addon4" type="button" className="btn"><FontAwesomeIcon icon={faSearch} /></button>
                                                </div>
                                                <input 
                                                    type="search" 
                                                    placeholder="Search..." 
                                                    className="form-control" 
                                                    onChange={(e) => this.setState({ envSearchText: e.target.value })} 
                                                    value={this.state.envSearchText} 
                                                />
                                            </div>
                                            {this.state.envSearchText && (
                                                <button className="btn btn-clear" title="clear" onClick={() => this.setState({ envSearchText: '' })}>
                                                    <i className="icon-close"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="qd-env-dropdown-menu__body">
                                        <Dropdown.Header>Environment</Dropdown.Header>
                                        {filteredEnvOptions.length > 0 ? (
                                            filteredEnvOptions.map((env) => (
                                                <Dropdown.Item
                                                    key={env.value}
                                                    className={`dropdown-item--all ${selectedEnvOption.value === env.value ? 'active' : ''}`}
                                                    onClick={() => {
                                                        if (onEnvironmentChange) onEnvironmentChange(env.value);
                                                    }}
                                                >
                                                    <span className="env-tag" style={{ backgroundColor: env.color }}>
                                                        {env.label[0].toUpperCase()}
                                                    </span>
                                                    <span className="menu-text">{env.label}</span>
                                                </Dropdown.Item>
                                            ))
                                        ) : (
                                            <div className="alert_notree-search-match"><p>No environment found</p></div>
                                        )}
                                    </div>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <label className="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-[#374151] dark:tw-text-white tw-cursor-pointer tw-select-none tw-mb-0">
                            <input
                                type="checkbox"
                                checked={showMappedKpis !== false}
                                onChange={onShowMappedToggle}
                                className="tw-w-[15px] tw-h-[15px] tw-rounded tw-accent-blue-600 tw-cursor-pointer"
                            />
                            <span className="tw-whitespace-nowrap">Include unmapped KPIs</span>
                        </label>
                    </div>

                    <div className="tw-w-full md:tw-flex-1 md:tw-max-w-[700px]">
                        <DomainMappingSearchBar
                            searchVal={searchVal}
                            onSearchChange={onSearchChange}
                            businesses={businesses}
                            functions={functions}
                            kpis={kpis}
                            filterBusinesses={filterBusinesses}
                            onBusinessFilterChange={onBusinessFilterToggle}
                            filterFunctions={filterFunctions}
                            onFunctionFilterChange={onFunctionFilterChange}
                            filterKpis={filterKpis}
                            onKpiFilterChange={onKpiFilterChange}
                            filterStatus={filterStatus}
                            onStatusFilterChange={onStatusFilterChange}
                            clearAllFilters={clearAllFilters}
                            matchingCount={matchingCount}
                        />
                    </div>

                    <div className="tw-flex tw-items-center tw-justify-between md:tw-justify-end tw-gap-3 tw-w-full md:tw-w-auto tw-shrink-0">
                        <div className="tw-flex tw-items-center tw-gap-2.5">
                            {!isFullyExpanded ? (
                                <button
                                    type="button"
                                    onClick={onExpandAll}
                                    className="tw-h-9 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg tw-bg-white dark:tw-bg-[#2B2B40] tw-text-[#374151] dark:tw-text-white tw-text-[11.5px] tw-font-semibold tw-px-2.5 tw-transition-colors hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-whitespace-nowrap"
                                >
                                    <ChevronsDown className="tw-w-3.5 tw-h-3.5" />
                                    <span>Expand all</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onCollapseAll}
                                    className="tw-h-9 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg tw-bg-white dark:tw-bg-[#2B2B40] tw-text-[#374151] dark:tw-text-white tw-text-[11.5px] tw-font-semibold tw-px-2.5 tw-transition-colors hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-whitespace-nowrap"
                                >
                                    <ChevronsUp className="tw-w-3.5 tw-h-3.5" />
                                    <span>Collapse all</span>
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={onAddMappingClick}
                            className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-[#EF4444] hover:tw-bg-[#DC2626] tw-rounded-lg tw-transition-colors tw-border-none tw-cursor-pointer tw-whitespace-nowrap"
                        >
                            <Plus size={16} />
                            Add mapping
                        </button>
                    </div>
                </div>
            </div>
        </>
        );
    }
}

export default DomainMappingToolbar;
